import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { formatNGN } from "@/lib/api";

const CORE_W = 500;

const Range = ({ id, label, value, display, min, max, step, onChange }) => (
  <div>
    <div className="flex items-baseline justify-between gap-4">
      <label htmlFor={id} className="text-sm font-medium" style={{ color: "#F2F0EA" }}>{label}</label>
      <span className="font-mono text-sm sunset-text font-semibold whitespace-nowrap">{display}</span>
    </div>
    <input
      id={id}
      data-testid={id}
      type="range"
      className="range-input mt-3"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={label}
    />
  </div>
);

const Stat = ({ testid, label, value, sub }) => (
  <div className="p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}>
    <div className="text-[10px] uppercase tracking-[0.2em] font-mono" style={{ color: "rgba(242,240,234,0.55)" }}>{label}</div>
    <div data-testid={testid} className="font-serif mt-2 sunset-text" style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", lineHeight: 1 }}>{value}</div>
    {sub && <div className="text-xs mt-2" style={{ color: "rgba(242,240,234,0.6)" }}>{sub}</div>}
  </div>
);

const computeSavings = ({ hours, ordW, cost, corePrice }) => {
  const savedW = ordW - CORE_W;
  const pct = Math.round((1 - CORE_W / ordW) * 100);
  const kwhYear = Math.round((savedW / 1000) * hours * 52);
  const nairaYear = Math.round(kwhYear * cost);
  const paybackMonths = corePrice > 0 && nairaYear > 0 ? Math.max(1, Math.ceil(corePrice / (nairaYear / 12))) : null;
  return { savedW, pct, kwhYear, nairaYear, paybackMonths };
};

const Controls = ({ hours, setHours, ordW, setOrdW, cost, setCost }) => (
  <div className="lg:col-span-5 space-y-8">
    <Range id="calc-hours" label="Ironing per week" value={hours} display={`${hours} hr${hours > 1 ? "s" : ""}`} min={1} max={20} step={1} onChange={setHours} />
    <Range id="calc-wattage" label="Your current iron" value={ordW} display={`${ordW.toLocaleString()}W`} min={1500} max={2400} step={100} onChange={setOrdW} />
    <Range id="calc-cost" label="Your power cost (gen fuel / tariff)" value={cost} display={`₦${cost}/kWh`} min={100} max={500} step={25} onChange={setCost} />
    <p className="text-xs leading-relaxed" style={{ color: "rgba(242,240,234,0.5)" }}>
      Estimates only — based on the CORE 500's 500W rated draw. Actual savings
      depend on fabric, settings and your power setup.
    </p>
  </div>
);

const Results = ({ r, corePrice, onAskAI }) => (
  <div className="lg:col-span-7">
    <div className="grid sm:grid-cols-2 gap-4">
      <Stat testid="calc-result-load" label="Load cut" value={`${r.savedW.toLocaleString()}W less`} sub={`${r.pct}% lighter on your inverter & battery`} />
      <Stat testid="calc-result-kwh" label="Energy saved / year" value={`${r.kwhYear.toLocaleString()} kWh`} sub="Power your battery keeps instead" />
      <Stat testid="calc-result-naira" label="Savings / year" value={formatNGN(r.nairaYear)} sub="At your power cost above" />
      <Stat
        testid="calc-result-payback"
        label="Pays for itself in"
        value={r.paybackMonths ? `~${r.paybackMonths} month${r.paybackMonths > 1 ? "s" : ""}` : "—"}
        sub={corePrice ? `CORE 500 costs ${formatNGN(corePrice)}` : ""}
      />
    </div>
    <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
      <Link to="/products/CORE-500" data-testid="calc-shop-cta" className="btn-brand justify-center">
        Start saving — shop the CORE 500 <ArrowRight size={16} />
      </Link>
      <button data-testid="calc-ai-btn" onClick={onAskAI} className="btn-outline justify-center" style={{ color: "#F2F0EA", borderColor: "rgba(255,255,255,0.3)" }}>
        <MessageCircle size={16} /> Get a detailed AI estimate
      </button>
    </div>
  </div>
);

export default function SavingsCalculator({ corePrice = 0 }) {
  const [hours, setHours] = useState(4);
  const [ordW, setOrdW] = useState(2000);
  const [cost, setCost] = useState(300);
  const results = computeSavings({ hours, ordW, cost, corePrice });
  const askAI = () => window.dispatchEvent(new CustomEvent("sp:open-chat", { detail: { mode: "calculator" } }));

  return (
    <section id="calculator" data-testid="savings-calculator" className="py-20 md:py-28" style={{ scrollMarginTop: "90px" }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="eyebrow">Battery &amp; energy savings</div>
        <h2 className="display mt-4 max-w-3xl" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
          How much will <span className="sunset-text">you</span> save?
        </h2>
        <p className="mt-5 max-w-2xl text-base sm:text-lg" style={{ color: "var(--ink-soft)" }}>
          Slide to match your home. We compare your current iron against the CORE 500's
          500W rated draw — live, no signup.
        </p>

        <div className="mt-10 glossy-deep rounded-2xl p-7 sm:p-10 md:p-12 relative overflow-hidden">
          <div className="sunset-ambient" style={{ top: "-260px", right: "-260px", width: "500px", height: "500px", opacity: 0.4 }} />
          <div className="relative z-10 grid lg:grid-cols-12 gap-10">
            <Controls hours={hours} setHours={setHours} ordW={ordW} setOrdW={setOrdW} cost={cost} setCost={setCost} />
            <Results r={results} corePrice={corePrice} onAskAI={askAI} />
          </div>
        </div>
      </div>
    </section>
  );
}
